"use client";

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/axios";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";

export default function MarketingWhatsAppPage() {
  const [ids, setIds] = useState("");
  const [template, setTemplate] = useState("");
  const [media, setMedia] = useState("");
  const [result, setResult] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      const customer_ids = ids
        .split(/[\s,]+/)
        .map((s) => Number(s.trim()))
        .filter((n) => Number.isFinite(n) && n > 0);
      const { data } = await api.post<{ sent: number; failed_or_skipped: number }>(
        "/api/whatsapp/bulk-send",
        {
          customer_ids,
          message_template: template.trim(),
          media_url: media.trim() || undefined,
        },
      );
      return data;
    },
    onSuccess: (d) => {
      setResult(`Sent: ${d.sent}, failed/skipped: ${d.failed_or_skipped}`);
    },
    onError: (e: unknown) => {
      const msg =
        typeof e === "object" &&
        e !== null &&
        "response" in e &&
        typeof (e as { response?: { data?: { error?: string } } }).response?.data?.error ===
          "string"
          ? (e as { response: { data: { error: string } } }).response.data.error
          : "Request failed";
      setResult(msg);
    },
  });

  return (
    <div className="space-y-6 p-6 max-w-2xl">
      <Link
        to="/marketing"
        className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "gap-2 inline-flex")}
      >
        <ArrowLeft className="h-4 w-4" />
        Marketing
      </Link>
      <h1 className="text-2xl font-semibold tracking-tight">WhatsApp bulk</h1>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Send template (WATI)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2">
            <Label>Customer IDs (comma or space)</Label>
            <Textarea
              rows={3}
              value={ids}
              onChange={(e) => setIds(e.target.value)}
              placeholder="1, 2, 5"
            />
          </div>
          <div className="grid gap-2">
            <Label>Template name</Label>
            <Input value={template} onChange={(e) => setTemplate(e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label>Media URL (optional)</Label>
            <Input value={media} onChange={(e) => setMedia(e.target.value)} />
          </div>
          <Button
            type="button"
            disabled={mut.isPending}
            onClick={() => {
              setResult(null);
              void mut.mutateAsync();
            }}
          >
            {mut.isPending ? "Sending…" : "Send"}
          </Button>
          {result && <p className="text-sm text-muted-foreground">{result}</p>}
        </CardContent>
      </Card>
    </div>
  );
}
